/**
 * Generic Comment System Component
 *
 * Renders comment pins for the current page and manages comment modals.
 * Integrates with Firestore-backed comment messaging system.
 *
 * Features:
 * - Displays comment pins at stored X/Y coordinates
 * - Adjusts pin position based on scroll offset
 * - Opens comment modals when pins are clicked
 * - Generates unique channel IDs for each comment pin
 * - Delete/resolve functionality for pins
 * - Deep link support with ?comment=id&scroll=Y URL parameters
 *
 * Reference: .claude/plans/comment-infra-shared-migration.md
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MessageCircle, CheckCircle } from 'lucide-react';
import { useCommentMode, type CommentPin } from '@/shared/contexts/CommentModeProvider';
import { useLocation, useNavigate } from 'react-router-dom';
import { CommentModal } from './CommentModal';
import { CommentPinService } from '@/shared/services/commentPinService';
import { cn } from '@/lib/utils';
import { log } from '@/lib/logger';

/**
 * Comment Pin Marker Component
 * Displays a clickable pin icon at the comment location
 */
interface CommentPinMarkerProps {
  pin: CommentPin;
  onClick: () => void;
  isActive: boolean;
  isHighlighted?: boolean;
}

const CommentPinMarker: React.FC<CommentPinMarkerProps> = ({
  pin,
  onClick,
  isActive,
  isHighlighted
}) => {
  // Listen to scroll of main content container, fallback to window
  const [scrollTop, setScrollTop] = React.useState(0);

  React.useEffect(() => {
    const scrollContainer =
      document.querySelector('main[class*="overflow-auto"]') ||
      document.querySelector('main[class*="overflow-y-auto"]');

    if (scrollContainer instanceof HTMLElement) {
      const handleScroll = () => setScrollTop(scrollContainer.scrollTop);
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      setScrollTop(scrollContainer.scrollTop);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }

    const handleScrollWindow = () => setScrollTop(window.scrollY);
    window.addEventListener('scroll', handleScrollWindow, { passive: true });
    setScrollTop(window.scrollY);
    return () => window.removeEventListener('scroll', handleScrollWindow);
  }, []);

  const adjustedY = pin.y - (scrollTop - pin.scrollY);

  return createPortal(
    <button
      onClick={onClick}
      className={cn(
        'comment-pin absolute p-2 rounded-full shadow-lg',
        isHighlighted && 'ring-4 ring-yellow-400',
        pin.resolved
          ? 'bg-gray-400 text-white hover:bg-gray-500'
          : isActive
            ? 'bg-purple-500 text-white scale-105'
            : 'bg-orange-400 text-white hover:bg-orange-600 hover:scale-105'
      )}
      style={{
        left: `${pin.x}px`,
        top: `${adjustedY}px`,
        position: 'absolute',
        zIndex: 40
      }}
      title={pin.resolved ? 'Resolved comment' : 'View comment'}
      aria-label={pin.resolved ? 'Resolved comment' : 'View comment'}
    >
      {pin.resolved ? (
        <CheckCircle className="h-4 w-4" />
      ) : (
        <MessageCircle className="h-4 w-4" />
      )}
    </button>,
    document.body
  );
};

/**
 * Generic Comment System
 * Renders all comment pins for the current page and manages modals
 */
export const GenericCommentSystem: React.FC = () => {
  const { commentPins, activePinId, setActivePinId, removeCommentPin, resolveCommentPin } = useCommentMode();
  const location = useLocation();
  const navigate = useNavigate();
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const [highlightedPinId, setHighlightedPinId] = useState<string | null>(null);

  // Update modal position when active pin changes (including newly created pins)
  useEffect(() => {
    if (activePinId) {
      const pin = commentPins.find(p => p.id === activePinId);
      if (pin) {
        // Calculate viewport position
        const viewportY = pin.y + (pin.scrollY - window.scrollY);
        setModalPosition({
          top: viewportY,
          left: pin.x,
        });
      }
    }
  }, [activePinId, commentPins]);

  // Handle pin click - open modal
  const handlePinClick = useCallback((pin: CommentPin) => {
    log.info('[GENERIC-COMMENT] Opening comment modal for pin:', pin.id);
    
    // Calculate viewport position
    const viewportY = pin.y + (pin.scrollY - window.scrollY);
    setModalPosition({
      top: viewportY,
      left: pin.x,
    });
    
    setActivePinId(pin.id);
  }, [setActivePinId]);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    log.info('[GENERIC-COMMENT] Closing comment modal');
    setActivePinId(null);
    
    // Clear URL params when closing modal
    const params = new URLSearchParams(location.search);
    if (params.has('comment') || params.has('scroll')) {
      params.delete('comment');
      params.delete('scroll');
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [setActivePinId, location.search, navigate]);

  // Get room name for comment pin from CommentPinService
  // This ensures consistency with Firestore storage
  const getRoomNameForPin = useCallback((pin: CommentPin): string => {
    const channelId = CommentPinService.getChannelIdForPin(pin);
    log.debug('[GENERIC-COMMENT] Generated channel ID for pin:', {
      pinId: pin.id,
      channelId,
      domain: pin.domain,
      pageUrl: pin.pageUrl,
      createdAt: pin.createdAt
    });
    return channelId;
  }, []);

  // Get active pin
  const activePin = useMemo(() => {
    return commentPins.find(p => p.id === activePinId);
  }, [commentPins, activePinId]);

  // Get channel ID for active pin (used by CommentModal's useCommentMessages)
  const activeChannelId = useMemo(() => {
    return activePin ? getRoomNameForPin(activePin) : '';
  }, [activePin, getRoomNameForPin]);

  const deepLinkHandledRef = useRef<string | null>(null);

  // Handle deep linking from URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const commentId = params.get('comment');

    if (!commentId) {
      deepLinkHandledRef.current = null;
      return;
    }

    // Avoid re-opening the modal on every commentPins update
    if (deepLinkHandledRef.current === commentId) return;

    log.info('[GENERIC-COMMENT] Deep link detected:', { commentId });

    const pin = commentPins.find(p => p.id === commentId);
    if (pin) {
      deepLinkHandledRef.current = commentId;

      const scrollY = params.get('scroll');
      if (scrollY) {
        window.scrollTo({ top: parseInt(scrollY), behavior: 'smooth' });
      }

      setHighlightedPinId(commentId);

      setTimeout(() => {
        handlePinClick(pin);
        setTimeout(() => setHighlightedPinId(null), 2000);
      }, scrollY ? 500 : 100);
    } else {
      log.warn('[GENERIC-COMMENT] Pin not found for deep link:', commentId);
    }
  }, [location.search, commentPins, handlePinClick]);

  return (
    <>
      {/* Render pins directly - they'll position relative to document */}
      {commentPins.map((pin) => (
        <CommentPinMarker
          key={pin.id}
          pin={pin}
          onClick={() => handlePinClick(pin)}
          isActive={pin.id === activePinId}
          isHighlighted={pin.id === highlightedPinId}
        />
      ))}

      {/* Render comment modal for active pin */}
      {activePin && activePinId && (
        <CommentModal
          key={activePinId}
          channelId={activeChannelId}
          entryId={activePin.id}
          workflowId={activePin.domain}
          workflowName={activePin.pageUrl}
          isOpen={true}
          onClose={handleModalClose}
          onResolve={() => {
            log.info('[GENERIC-COMMENT] Modal resolve triggered for pin:', activePinId);
            resolveCommentPin(activePinId);
            handleModalClose();
          }}
          onDelete={() => {
            log.info('[GENERIC-COMMENT] Modal delete triggered for pin:', activePinId);
            if (confirm('Are you sure you want to delete this comment?')) {
              removeCommentPin(activePinId);
              handleModalClose();
            }
          }}
          position={modalPosition}
          pinContext={{
            pageUrl: activePin.pageUrl,
            scrollY: activePin.scrollY,
            pinId: activePin.id
          }}
          useEnhancedUI={true}
        />
      )}
    </>
  );
};

